import {match} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import type {RowData} from '../types/RowData';
import type {SelectDatagridRowsTarget} from './selectDatagridRows';

type ResolveExternalRowSelectionParams = {
  /** Currently loaded (current page) rows — the only rows 'set'/'add' can target. */
  rows: RowData[];
  selectedRowIds: Set<string>;
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  target: SelectDatagridRowsTarget;
};

// O(n) Set<string> membership equality — ramda's equals on Sets does nested
// deep-equals scans with per-pair stack allocations (O(n²)), so it is avoided here.
const haveSameMembers = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every((id) => b.has(id));

/**
 * Resolves an external selection request (selectDatagridRows) against the currently
 * loaded rows. Returns the next selectedRowIds Set, or null when nothing should change:
 * - selection is disabled (rowSelectMode 'NONE'),
 * - a 'set' request named targets but matched nothing (unmatched targets are ignored,
 *   never a wipe — clearing is explicit via `{rowIds: []}`),
 * - the result would hold more than one row in 'SINGLE' mode,
 * - the resolved selection has the same members as the current one (no-op guard —
 *   setSelectedRowIds always stores a new reference, which would re-notify subscribers).
 *
 * 'SINGLE' mode mirrors row-click behavior (setSingleSelection): a one-row request
 * replaces the current selection, so mode 'add' is normalized to 'set' in that case.
 *
 * Mode 'remove' matches raw requested ids (not just loaded rows): a selection can
 * outlive its page (setRows never prunes it), and removing an unknown id is harmless.
 */
export const resolveExternalRowSelection = (
  params: ResolveExternalRowSelectionParams
): Set<string> | null => {
  if (params.rowSelectMode === 'NONE') {
    return null;
  }

  const target = params.target;
  const mode = target.mode ?? 'set';

  // Deduplicate requested ids so duplicates (e.g. domain items sharing a row) cannot
  // defeat the SINGLE-mode one-row normalization below. Predicate matches are already
  // unique — row ids are unique within a page.
  const requestedIds = isNotNil(target.predicate)
    ? params.rows.filter(target.predicate).map((row) => row.id)
    : [...new Set(target.rowIds ?? [])];

  const loadedIds = new Set(params.rows.map((row) => row.id));
  const matchedIds =
    mode === 'remove' ? requestedIds : requestedIds.filter((id) => loadedIds.has(id));

  // A 'set' that named targets but matched nothing must not wipe the current selection.
  // The clear-intent check is a POSITIVE test on the rowIds variant — inferring it from
  // predicate absence would make a malformed target (or a future third variant) read as
  // "clear" and wipe the selection.
  const isExplicitClear = isNotNil(target.rowIds) && requestedIds.length === 0;
  if (mode === 'set' && matchedIds.length === 0 && !isExplicitClear) {
    return null;
  }

  // Click parity in SINGLE mode: one targeted row REPLACES the selection (like
  // setSingleSelection — keep the two policies in sync), so a one-row 'add' is
  // normalized to 'set' before computing the result.
  const effectiveMode =
    params.rowSelectMode === 'SINGLE' && mode !== 'remove' && matchedIds.length === 1
      ? 'set'
      : mode;

  const next = match(effectiveMode)
    .with('set', () => new Set(matchedIds))
    .with('add', () => new Set([...params.selectedRowIds, ...matchedIds]))
    .with('remove', () => {
      const result = new Set(params.selectedRowIds);
      matchedIds.forEach((id) => result.delete(id));
      return result;
    })
    .exhaustive();

  if (params.rowSelectMode === 'SINGLE' && next.size > 1) {
    return null;
  }

  if (haveSameMembers(next, params.selectedRowIds)) {
    return null;
  }

  return next;
};
